import * as React from 'react';
import {Mutation} from 'react-apollo';
// @ts-expect-error
import type {DataProxy} from 'react-apollo';

import {Button} from 'shells/button/button';

import type {
    AddEntityToConstantContact as AddEntityToConstantContactMutation,
    AddEntityToConstantContactVariables as AddEntityToConstantContactMutationVariables,
    GetCtctContacts as GetCtctContactsQuery,
} from 'nutshell-graphql-types';

import {ConstantContactPopover} from '../../core/constant-contact/constant-contact-popover/constant-contact-popover';
import {GetSessionUser} from '../../core/get-session-user';

import ADD_ENTITY_TO_CONSTANT_CONTACT from './graphql/mutations/add-entity-to-constant-contact.graphql';
import GET_CTCT_CONTACTS from './graphql/queries/get-ctct-contacts.graphql';

import styles from './add-to-constant-contact.module.css';

type Props = {
    entityId: string;
    buttonText: string;
    onSuccess?: () => void;
};

class AddEntityMutation extends Mutation<
    AddEntityToConstantContactMutation,
    AddEntityToConstantContactMutationVariables
> {}

AddToConstantContact.defaultProps = {
    buttonText: '+ Send to Constant Contact',
};

export function AddToConstantContact({entityId, buttonText, onSuccess}: Props) {
    const buttonRef = React.useRef<HTMLElement | null | undefined>(null);
    const [showPopover, setShowPopover] = React.useState<boolean>(false);

    return (
        <GetSessionUser>
            {({user}) => (
                <AddEntityMutation mutation={ADD_ENTITY_TO_CONSTANT_CONTACT}>
                    {(addPersonToConstantContact) => {
                        return (
                            <div className={styles.adder}>
                                <Button
                                    variant='text-primary'
                                    getButtonRef={buttonRef}
                                    onClick={() => {
                                        setShowPopover(true);
                                    }}
                                >
                                    {buttonText}
                                </Button>
                                {showPopover && (
                                    <ConstantContactPopover
                                        anchor={buttonRef.current}
                                        submitButtonText='Send'
                                        canAccessIntegrations={Boolean(
                                            user && user.permissions.canAccessSetup
                                        )}
                                        onCancel={() => {
                                            setShowPopover(false);
                                        }}
                                        onSuccess={() => {
                                            setShowPopover(false);
                                            {
                                                // eslint-disable-next-line @typescript-eslint/no-unused-expressions
                                                onSuccess
                                                    ? onSuccess()
                                                    : Popmini.open(
                                                          'Successfully sent to Constant Contact.',
                                                          {
                                                              attachTo: buttonRef.current,
                                                              classes: 'type-message',
                                                              orientation: 'west',
                                                          }
                                                      );
                                            }
                                        }}
                                        onSubmit={(data) => {
                                            if (data.listId) {
                                                return addPersonToConstantContact({
                                                    variables: {
                                                        input: {
                                                            entityId: entityId,
                                                            listId: data.listId,
                                                        },
                                                    },
                                                    update: (proxy: DataProxy, mutationResults) => {
                                                        const entity =
                                                            mutationResults &&
                                                            mutationResults.data &&
                                                            mutationResults.data
                                                                .constantContactAddEntityToList
                                                                .entity;
                                                        if (!entity) return;

                                                        const cacheResult:
                                                            | GetCtctContactsQuery
                                                            | null
                                                            | undefined = proxy.readQuery({
                                                            query: GET_CTCT_CONTACTS,
                                                            variables: {
                                                                entityId: entityId,
                                                            },
                                                        });

                                                        if (!cacheResult || !cacheResult.coreEntity)
                                                            return;

                                                        proxy.writeQuery({
                                                            query: GET_CTCT_CONTACTS,
                                                            variables: {
                                                                personId: entityId,
                                                            },
                                                            data: {
                                                                coreEntity: {
                                                                    ...cacheResult.coreEntity,
                                                                    ...entity,
                                                                },
                                                            },
                                                        });
                                                    },
                                                });
                                            }

                                            return Promise.reject(
                                                'Choose a constant contact list.'
                                            );
                                        }}
                                    />
                                )}
                            </div>
                        );
                    }}
                </AddEntityMutation>
            )}
        </GetSessionUser>
    );
}
